import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Loader2, ArrowRight, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import api from "@/lib/api";

/**
 * Hume Shop — post-Stripe success page. Polls the backend so the moment
 * Stripe reports "paid" the order flips from "pending" to "fulfilling".
 */
export default function HumeShopSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (!sessionId) { setBusy(false); return; }
    let stopped = false;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/shop/hume/order/${sessionId}`);
        if (stopped) return;
        setOrder(data.order);
        if (data.order?.payment_status === "paid" || tries > 8) {
          setBusy(false); return;
        }
      } catch { /* keep trying */ }
      tries += 1;
      setTimeout(poll, 1500);
    };
    poll();
    return () => { stopped = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#050810] text-white" data-testid="hume-shop-success">
      <SiteNav />
      <section className="max-w-3xl mx-auto px-6 py-32">
        <div className="ink-card rounded-2xl p-8">
          {busy ? (
            <div className="flex items-center gap-3 text-white/80">
              <Loader2 className="h-5 w-5 animate-spin" /> Confirming your order with Stripe…
            </div>
          ) : order?.payment_status === "paid" ? (
            <>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-emerald-500/20 border border-emerald-500/50 flex items-center justify-center">
                  <Check className="h-5 w-5 text-emerald-300" />
                </div>
                <div className="text-[10px] tracking-[0.3em] uppercase font-bold text-emerald-300">Payment received</div>
              </div>
              <h1 className="font-display font-black text-3xl text-white">Your Hume Band is on the way.</h1>
              <p className="mt-3 text-white/70">
                Order <span className="font-mono-num text-white">{order.order_id}</span> — thank you, {order.name}. A confirmation email has been sent to <b className="text-white">{order.email}</b>.
              </p>
              <div className="mt-6 grid sm:grid-cols-3 gap-3 text-sm">
                <Card icon={Package} label="Quantity"   value={`${order.quantity} × Hume Band`} />
                <Card icon={Package} label="Warranty"   value={order.warranty_10yr ? "10-year upgrade" : "1-year (included)"} />
                <Card icon={Package} label="Total paid" value={`$${(order.total_usd || 0).toFixed(2)} USD`} />
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/platform">
                  <Button className="text-white font-bold text-sm px-6" style={{ backgroundColor: "var(--brand-secondary)" }}>
                    See how it plugs in <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/sports">
                  <Button variant="ghost" className="text-white/90 border border-white/20 hover:bg-white/10 font-bold text-sm">
                    Explore sport packages
                  </Button>
                </Link>
              </div>
            </>
          ) : (
            <>
              <h1 className="font-display font-black text-2xl text-white">Order status pending</h1>
              <p className="mt-2 text-white/60">We haven't received confirmation from Stripe yet. Please refresh in a moment or contact <a className="text-fuchsia-300 underline" href="mailto:orders@biohackersports.com">orders@biohackersports.com</a>.</p>
            </>
          )}
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}

function Card({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-white/50 mb-1">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="text-white font-semibold">{value}</div>
    </div>
  );
}
